import { debug } from 'zf-web-infra';
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Watermark } from 'antd';
import * as Base from '@base';
import { Icons, LazyImage } from '@common';
import { Block } from '../block';
import { useTranslation } from '../i18n';
import { useNormalize } from './useNormalize';
import { ImageProgress } from './progress';
import { ImageAction } from './action';
import { useDisableContextMenu } from './useDisableContextMenu';

import styles from './style.module.less';
import type { ImageItem, ImageProps } from './types';
import 'photoswipe/style.css';

/**
 * 图片组件，允许多张图片。
 * 支持图片的更换，删除，预览
 * TODO: 多国语言
 * @param props
 * @returns
 */
export function Image(props: ImageProps): ReactElement | null {
  // 尝试设置图片尺寸, alioss 转换图片显示
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { images } = useNormalize(props.src, props.baseName, props.internal);
  const isClickPreview = useMemo(() => props.preview === false, [props.preview]);
  const image = useMemo(() => images[current], [images, current]);
  const [previewImage, setPreviewImage] = useState<ImageItem>(images[current]);
  const { t } = useTranslation();
  debug.component('images', images);

  const pauseVideo = useCallback(() => {
    if (videoRef.current != null) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [videoRef]);

  const setCurrentSafe = useCallback(async (index: number) => {
    pauseVideo();
    let imgIndex = index;
    if (index < 0) {
      imgIndex = 0;
    } else if (index >= images.length) {
      imgIndex = images.length - 1;
    }
    setVisible(true);
    setCurrent(imgIndex);
    const image = images[imgIndex];
    setPreviewImage(image);
  }, [images]);

  const handlePreview = useCallback(async (index: number) => {
    try {
      const targetImage = images[index];
      if (targetImage == null) return;
      await setCurrentSafe(index);
    } catch (error) {
      console.warn('图片预加载失败:', error);
      // 即使预加载失败也显示预览
      await setCurrentSafe(index);
      setVisible(true);
    }
  }, [setCurrentSafe]);

  useEffect(() => {
    if (!visible) {
      pauseVideo();
    }
  }, [visible]);

  const showActions = useCallback((type?: string) => {
    const graffiti = type === 'video' ? false : props.graffiti === true;

    return props.preview !== false
      || (!props.preview && type === 'video')
      || props.edit !== false
      || props.remove !== false
      || graffiti;
  }, [props.edit, props.remove, props.preview, props.graffiti]);

  const onImgContainerClick = useCallback(async (index: number) => {
    if (!isClickPreview) {
      return;
    }
    await handlePreview(index);
  }, [isClickPreview, handlePreview]);

  // 设置预览时，禁止鼠标右键行为
  useDisableContextMenu(visible, isClickPreview);

  return (
    <Base.Image.PreviewGroup
      items={images}
      preview={{
        visible,
        getContainer: '.ant-app',
        current,
        minScale: 0.5,
        rootClassName: styles.previewRoot, // 新增：用于 scoped 覆盖预览层样式
        toolbarRender: (
          // eslint-disable-next-line @typescript-eslint/naming-convention
          _,
          {
            current,
            transform: { scale },
            actions: {
              onFlipY,
              onFlipX,
              onRotateLeft,
              onRotateRight,
              onZoomOut,
              onZoomIn
            }
          }
        ) => (
          <Base.Space size={12} className={styles.toolbarWrapper}>
            <Block if={props.download === true}>
              <Icons name='DownloadOutlined' className={styles.icon} onClick={() => props?.onDownload?.(current)} />
            </Block>
            {image?.type !== 'video' && <>
              <Icons className={styles.icon} name='SwapOutlined' rotate={90} onClick={onFlipY} />
              <Icons className={styles.icon} name='SwapOutlined' onClick={onFlipX} />
              <Icons className={styles.icon} name='RotateLeftOutlined' onClick={onRotateLeft} />
              <Icons className={styles.icon} name='RotateRightOutlined' onClick={onRotateRight} />
              <Icons className={styles.icon} name='ZoomOutOutlined' disabled={scale === 1} onClick={onZoomOut} />
              <Icons className={styles.icon} name='ZoomInOutlined' disabled={scale === 50} onClick={onZoomIn} />
            </>}
          </Base.Space>
        ),
        onChange: setCurrentSafe,
        onVisibleChange: setVisible,
        imageRender: node => {
          if (props.watermarkContent != null) {
            return (
              <Watermark
                content={props.watermarkContent}
                font={props.watermarkFont}
                className={styles.watermark}
              >
                <Block if={previewImage?.type !== 'video'}>
                  {cloneElement(node, { src: previewImage?.src })}
                </Block>
                <Block if={previewImage?.type === 'video'}>
                  <video
                    className={styles.videoItem}
                    ref={videoRef}
                    controls
                    src={previewImage?.src}
                    controlsList={props.download === true ? undefined : 'nodownload'}
                  >
                    {t('您的浏览器不支持 video 标签')}
                  </video>
                </Block>
              </Watermark>
            );
          }
          return (
            <>
              <Block if={previewImage?.type !== 'video'}>
                {cloneElement(node, { src: previewImage?.src })}
              </Block>
              <Block if={previewImage?.type === 'video'}>
                <video className={styles.videoItem} ref={videoRef} controls src={previewImage?.src}
                  controlsList={props.download === true ? undefined : 'nodownload'}>
                  {t('您的浏览器不支持 video 标签')}
                </video>
              </Block>
            </>
          );
        }
      }}>
      <div className={styles.imageContainer}>
        {images.map((image, index) => (
          <div className={[styles.imageItem, props.className].join(' ')}
            onClick={async () => await onImgContainerClick(index)}
            key={image.src}
          >
            <Block if={image.type === 'video'}>
              <Block if={image.thumbnail != null}>
                <LazyImage
                  width='100%'
                  alt={image.alt}
                  internal={props.internal}
                  src={image.thumbnail}
                  preview={isClickPreview ? false : undefined}
                />
              </Block>
              <Block if={image.thumbnail == null}>
                <video src={image.src} width='100%' poster={image.thumbnail} />
              </Block>
            </Block>

            <Block if={image.type !== 'video'}>
              <LazyImage
                width='100%'
                alt={image.alt}
                internal={props.internal}
                src={image.thumbnail ?? image.src}
                preview={isClickPreview ? false : undefined}
              />

              <Block if={image.percent != null}>
                <ImageProgress percent={image.percent} />
              </Block>
            </Block>

            {/* 视频展示时增加区分的标签 */}
            <Block if={image.type === 'video'}>
              <div className={styles.videoTag}>
                <span className={styles.videoText}>{t('视频')}</span>
              </div>
            </Block>

            <Block if={image.percent == null && showActions(image.type)}>
              <ImageAction
                preview={(image.type === 'video' && props.preview === false) ? true : props.preview}
                previewName={props.previewName}
                edit={props.edit}
                editName={props.editName}
                remove={props.remove}
                removeName={props.removeName}
                graffiti={image.type === 'image' ? props.graffiti : false}
                graffitiName={props.graffitiName}
                onGraffiti={() => props.onGraffiti?.(image, index)}
                onPreview={async () => await handlePreview(index)}
                onEdit={() => props.onEdit?.(index)}
                onRemove={() => props.onRemove?.(index)}
                mask={props.mask ?? true}
                isVideo={image.type === 'video'}
              />
            </Block>
          </div>
        ))}
      </div>
    </Base.Image.PreviewGroup>
  );
}
